import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const CONTINUE_OPTIONS = [
  { value: "yes", label: "Да" }, { value: "probably_yes", label: "Скорее да" },
  { value: "unsure", label: "Пока не уверен" }, { value: "no", label: "Нет" },
];
const PUBLICATION_OPTIONS = [{ value: "yes", label: "Да" }, { value: "no", label: "Нет" }];
const DEFAULT_POST_CONTENT = [
  { key: "clarity_rating", text: "Насколько понятным оказался Мастер заказа?", kind: "rating", options: [1, 2, 3, 4, 5].map((value) => ({ value: String(value), label: String(value) })), required: true },
  { key: "most_useful_text", text: "Что оказалось самым удобным или полезным?", kind: "textarea", required: true },
  { key: "confusing_text", text: "В каком месте ты запутался или не сразу понял, что делать?", kind: "textarea", required: true },
  { key: "missing_text", text: "Чего тебе не хватило?", kind: "textarea", required: true },
  { key: "priority_improvement_text", text: "Что бы ты изменил или улучшил в первую очередь?", kind: "textarea", required: true },
  { key: "continue_use", text: "Хотелось бы тебе продолжать пользоваться Мастером заказа?", kind: "radio", options: CONTINUE_OPTIONS, required: true },
  { key: "publication_consent", text: "Можно ли использовать часть твоего отзыва в материалах Anokina Studio?", kind: "radio", options: PUBLICATION_OPTIONS, required: false },
];
const TEST_IDS = {
  clarity_rating: "mo-feedback-clarity-rating", most_useful_text: "mo-feedback-most-useful",
  confusing_text: "mo-feedback-confusing", missing_text: "mo-feedback-missing",
  priority_improvement_text: "mo-feedback-priority-improvement", continue_use: "mo-feedback-continue-use",
  publication_consent: "mo-feedback-publication-consent",
};

function PageFrame({ children, preview = false }) {
  const nav = useNavigate();
  return (
    <main className="min-h-screen" style={{ background: "#EFE6D5" }} data-testid="mo-feedback-page">
      <div className="mx-auto px-5 sm:px-10 py-12 sm:py-16" style={{ maxWidth: 880 }}>
        <div className="mb-8 flex flex-wrap gap-3">
          <button type="button" onClick={() => nav(preview ? "/admin?tab=mo_testing" : "/master-order-testing")} data-testid="mo-feedback-back"
            className="h-9 px-2 rounded-xl text-[12px] tracking-[0.14em] uppercase" style={{ color: "#5B4326" }}>
            {preview ? "← Вернуться в панель управления" : "← Назад"}
          </button>
          {!preview && <button type="button" onClick={() => nav("/hall")} data-testid="mo-feedback-hall-top"
            className="h-9 px-4 rounded-xl text-[12px]" style={{ color: "#5B4326", boxShadow: "inset 0 0 0 1px #C7A97A" }}>
            Вернуться в Anokina Studio
          </button>}
        </div>
        {children}
      </div>
    </main>
  );
}

function Title({ children }) {
  return <header className="mb-8">
    <div className="text-[10.5px] tracking-[0.3em] uppercase mb-3" style={{ color: "#8B6F4B" }}>ANOKINA STUDIO · МАСТЕР ЗАКАЗА</div>
    <h1 className="font-serif leading-[1.1]" style={{ color: "#2A1D14", fontSize: "clamp(28px, 4.6vw, 40px)" }}>{children}</h1>
  </header>;
}
function Label({ children }) {
  return <div className="text-[10.5px] tracking-[0.18em] uppercase leading-relaxed" style={{ color: "#8B6F4B" }}>{children}</div>;
}
function ErrorText({ children }) {
  return <div className="mt-2 text-[12.5px]" style={{ color: "#8B3E2A" }} role="alert">{children}</div>;
}
function Button({ children, onClick, type = "button", secondary = false, disabled = false, testid }) {
  return <button type={type} onClick={onClick} disabled={disabled} data-testid={testid}
    className="h-11 px-6 rounded-xl text-[13px] disabled:opacity-60"
    style={secondary ? { color: "#5B4326", boxShadow: "inset 0 0 0 1px #C7A97A" } : { background: "#4A3728", color: "#F7EFE0" }}>
    {children}
  </button>;
}
function RadioGroup({ label, options, value, onChange, error, testid }) {
  return <div data-testid={testid}><Label>{label}</Label><div className="mt-3 flex flex-col gap-2">{options.map((option) => {
    const active = value === option.value;
    return <label key={option.value} className="flex items-center gap-3 h-11 px-4 rounded-md text-[14px] cursor-pointer"
      style={{ background: active ? "#F1E4CC" : "#FFFFFF", color: "#2A1D14", boxShadow: "inset 0 0 0 1px " + (active ? "#C7A97A" : "#E4D3B0") }}>
      <input type="radio" name={testid} checked={active} onChange={() => onChange(option.value)} className="accent-[#4A3728]" />
      {option.label}
    </label>;
  })}</div>{error && <ErrorText>{error}</ErrorText>}</div>;
}
function RatingGroup({ label, value, onChange, error, testid }) {
  return <div data-testid={testid}><Label>{label}</Label><div className="mt-3 flex flex-wrap gap-2">
    {[1, 2, 3, 4, 5].map((rating) => <button key={rating} type="button" onClick={() => onChange(rating)}
      aria-label={`${rating} из 5`} aria-pressed={value === rating}
      className="h-11 w-11 rounded-md text-[15px]"
      style={{ background: value === rating ? "#4A3728" : "#FFFFFF", color: value === rating ? "#F7EFE0" : "#2A1D14", boxShadow: "inset 0 0 0 1px #C7A97A" }}>
      {rating}
    </button>)}
  </div>{error && <ErrorText>{error}</ErrorText>}</div>;
}
function TextQuestion({ label, value, onChange, error, testid, rows = 4 }) {
  return <div><Label>{label}</Label><textarea value={value} onChange={(event) => onChange(event.target.value)}
    rows={rows} data-testid={testid} className="mt-2 w-full px-4 py-3 rounded-md text-[14px] bg-white outline-none resize-y"
    style={{ color: "#2A1D14", boxShadow: "inset 0 0 0 1px #E4D3B0" }} />{error && <ErrorText>{error}</ErrorText>}</div>;
}

export default function MasterOrderTestingFeedback() {
  const nav = useNavigate();
  const [search] = useSearchParams();
  const { user, initializing } = useAuth();
  const previewState = search.get("preview_state");
  const preview = !!previewState && ["owner", "administrator"].includes(user?.role);
  const [content, setContent] = useState({ post_trial: DEFAULT_POST_CONTENT, copy: {} });
  const [loading, setLoading] = useState(true);
  const [application, setApplication] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [answers, setAnswers] = useState({
    clarity_rating: null,
    most_useful_text: "",
    confusing_text: "",
    missing_text: "",
    priority_improvement_text: "",
    continue_use: "",
    publication_consent: "",
  });
  const set = (key, value) => setAnswers((current) => ({ ...current, [key]: value }));

  useEffect(() => {
    document.title = "Обратная связь — Мастер заказа — Anokina Studio";
  }, []);

  useEffect(() => {
    if (initializing) return;
    if (!user) {
      try { sessionStorage.setItem("post_login_redirect", "/master-order-testing/feedback"); } catch {}
      nav("/login", { replace: true });
      return;
    }
    let cancelled = false;
    Promise.all([
      preview ? Promise.resolve({ data: { status: previewState === "feedback_received" ? "feedback_received" : "completed_test" } }) : api.get("/master-order-testing/applications/mine"),
      api.get("/master-order-testing/content"),
    ])
      .then(([applicationResponse, contentResponse]) => { if (!cancelled) { setApplication(applicationResponse.data || null); if (contentResponse.data) setContent(contentResponse.data); } })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [initializing, user, nav, preview, previewState]);

  const eligibility = useMemo(() => application?.status || "no_application", [application]);

  const validate = () => {
    const next = {};
    for (const question of content.post_trial || DEFAULT_POST_CONTENT) {
      if (question.required && !String(answers[question.key] || "").trim()) next[question.key] = "Заполни этот ответ.";
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const submit = async (event) => {
    event.preventDefault();
    if (submitting || !validate()) return;
    if (preview) {
      setSubmitted(true);
      toast.success("Режим QA: обратная связь не создана");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/master-order-testing/feedback", {
        clarity_rating: answers.clarity_rating,
        most_useful_text: answers.most_useful_text.trim(),
        confusing_text: answers.confusing_text.trim(),
        missing_text: answers.missing_text.trim(),
        priority_improvement_text: answers.priority_improvement_text.trim(),
        continue_use: answers.continue_use,
        publication_consent: answers.publication_consent || null,
      });
      setSubmitted(true);
      toast.success("Обратная связь отправлена");
    } catch (error) {
      const detail = error?.response?.data?.detail;
      if (error?.response?.status === 409 && detail?.code === "feedback_exists") setSubmitted(true);
      else toast.error(formatApiError(error) || "Не удалось отправить обратную связь");
    } finally {
      setSubmitting(false);
    }
  };

  if (initializing || !user) return null;
  if (loading) return <PageFrame preview={preview}><div className="py-16 text-[13px]" style={{ color: "#8B6F4B" }}>Загружаем форму…</div></PageFrame>;

  if (submitted || eligibility === "feedback_received") return <PageFrame preview={preview}>
    <Title>Спасибо за обратную связь</Title>
    <p className="max-w-[62ch] text-[15px] leading-[1.65]" style={{ color: "#3E2E20" }}>
      Мы сохранили твои ответы. Они помогут нам улучшать Мастер заказа.
    </p>
    <div className="mt-10"><Button testid="mo-feedback-back-to-hall" onClick={() => nav(preview ? "/admin?tab=mo_testing" : "/hall")}>
      {preview ? "← Вернуться в панель управления" : "Вернуться в Anokina Studio"}
    </Button></div>
  </PageFrame>;

  if (eligibility !== "completed_test") return <PageFrame preview={preview}>
    <Title>Обратная связь пока недоступна</Title>
    <p className="max-w-[62ch] text-[15px] leading-[1.65]" style={{ color: "#3E2E20" }}>
      Форма откроется после завершения 24-часового знакомства с Мастером заказа.
    </p>
  </PageFrame>;

  return <PageFrame preview={preview}>
    <Title>{content.copy?.feedback_title || "Как прошло знакомство с Мастером заказа?"}</Title>
    <p className="max-w-[62ch] text-[15px] leading-[1.65]" style={{ color: "#3E2E20" }}>
      {content.copy?.feedback_intro || "Расскажи, как всё прошло. Нам особенно важно понять, что было удобно и где возникли вопросы."}
    </p>
    <form onSubmit={submit} className="mt-12 max-w-[62ch] space-y-8" data-testid="mo-feedback-form" noValidate>
      {(content.post_trial || DEFAULT_POST_CONTENT).map((question, index) => question.kind === "rating" ? (
        <RatingGroup key={question.key} label={`${index + 1}. ${question.text}`} value={answers[question.key]}
          onChange={(value) => set(question.key, value)} error={errors[question.key]} testid={TEST_IDS[question.key]} />
      ) : question.kind === "radio" ? (
        <div key={question.key}>
          <RadioGroup label={`${index + 1}. ${question.text}`} options={question.options} value={answers[question.key]}
            onChange={(value) => set(question.key, value)} error={errors[question.key]} testid={TEST_IDS[question.key]} />
          {question.key === "publication_consent" && <div className="mt-2 text-[12.5px]" style={{ color: "#8B6F4B" }}>Выбор не сделан по умолчанию.</div>}
        </div>
      ) : (
        <TextQuestion key={question.key} label={`${index + 1}. ${question.text}`} value={answers[question.key]}
          onChange={(value) => set(question.key, value)} error={errors[question.key]} testid={TEST_IDS[question.key]} />
      ))}
      <Button type="submit" testid="mo-feedback-submit-button" disabled={submitting}>
        {submitting ? "Отправляем…" : "Отправить обратную связь"}
      </Button>
    </form>
  </PageFrame>;
}
